/**
 * 页面关闭清理服务
 * 处理用户突然关闭页面时的资源清理工作
 */

import { audioManager } from './audio';
import { mqttPttService } from './mqttPttService';
import { ApiClient } from '../utils/apiClient';

export interface PageCleanupData {
  type: 'page_unload' | 'page_hidden' | 'page_blur';
  deviceId?: string;
  timestamp: number;
  userId?: string;
}

class PageCleanupService {
  private static instance: PageCleanupService;
  private isInitialized = false;
  private cleanupHandlers: Map<string, () => void> = new Map();

  private constructor() {}

  public static getInstance(): PageCleanupService {
    if (!PageCleanupService.instance) {
      PageCleanupService.instance = new PageCleanupService();
    }
    return PageCleanupService.instance;
  }

  /**
   * 初始化页面关闭监听
   */
  public initialize(): void {
    if (this.isInitialized) return;

    console.log('初始化页面关闭清理服务...');

    // 监听页面关闭事件
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    
    // 监听页面可见性变化（移动端切换应用时）
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // 监听页面焦点变化
    window.addEventListener('blur', this.handlePageBlur.bind(this));

    // 监听网络状态变化
    window.addEventListener('online', this.handleNetworkChange.bind(this));
    window.addEventListener('offline', this.handleNetworkChange.bind(this));

    this.isInitialized = true;
    console.log('页面关闭清理服务初始化完成');
  }

  /**
   * 注册清理处理器
   */
  public registerCleanupHandler(id: string, handler: () => void): void {
    this.cleanupHandlers.set(id, handler);
  }

  /**
   * 注销清理处理器
   */
  public unregisterCleanupHandler(id: string): void {
    this.cleanupHandlers.delete(id);
  }

  /**
   * 处理页面关闭事件
   */
  private async handleBeforeUnload(event: BeforeUnloadEvent): Promise<void> {
    console.log('页面即将关闭，开始清理工作...');
    
    try {
      // 执行所有注册的清理处理器
      this.cleanupHandlers.forEach((handler, id) => {
        try {
          handler();
          console.log(`清理处理器 ${id} 执行完成`);
        } catch (error) {
          console.error(`清理处理器 ${id} 执行失败:`, error);
        }
      });

      // 强制关闭PTT
      await this.forceClosePTT();

      // 停止音频录制
      await this.stopAudioRecording();

      // 发送页面关闭通知到后端
      await this.sendCleanupNotification('page_unload');

    } catch (error) {
      console.error('页面关闭清理失败:', error);
    }
  }

  /**
   * 处理页面可见性变化
   */
  private async handleVisibilityChange(): Promise<void> {
    if (document.visibilityState === 'hidden') {
      console.log('页面隐藏，执行清理工作...');
      
      try {
        // 强制关闭PTT
        await this.forceClosePTT();

        // 停止音频录制
        await this.stopAudioRecording();

        // 发送页面隐藏通知
        await this.sendCleanupNotification('page_hidden');

      } catch (error) {
        console.error('页面隐藏清理失败:', error);
      }
    }
  }

  /**
   * 处理页面失去焦点
   */
  private async handlePageBlur(): Promise<void> {
    console.log('页面失去焦点，执行清理工作...');
    
    try {
      // 强制关闭PTT
      await this.forceClosePTT();

      // 停止音频录制
      await this.stopAudioRecording();

      // 发送页面失焦通知
      await this.sendCleanupNotification('page_blur');

    } catch (error) {
      console.error('页面失焦清理失败:', error);
    }
  }

  /**
   * 处理网络状态变化
   */
  private handleNetworkChange(): void {
    const isOnline = navigator.onLine;
    console.log(`网络状态变化: ${isOnline ? '在线' : '离线'}`);
    
    if (!isOnline) {
      // 网络断开时执行清理
      this.handleVisibilityChange();
    }
  }

  /**
   * 强制关闭PTT
   */
  private async forceClosePTT(): Promise<void> {
    try {
      // 获取当前活跃设备
      const activeDevice = audioManager.getActiveDevice();
      if (activeDevice) {
        // 直接尝试关闭PTT，不检查状态
        try {
          await mqttPttService.pttOff(activeDevice);
          console.log('页面关闭时PTT已强制关闭');
        } catch (error) {
          console.log('PTT可能已经关闭或不存在:', error);
        }
      }
    } catch (error) {
      console.error('强制关闭PTT失败:', error);
    }
  }

  /**
   * 停止音频录制
   */
  private async stopAudioRecording(): Promise<void> {
    try {
      const audioState = audioManager.getState();
      if (audioState.isRecording) {
        await audioManager.stopRecording();
        console.log('页面关闭时音频录制已停止');
      }
    } catch (error) {
      console.error('停止音频录制失败:', error);
    }
  }

  /**
   * 发送清理通知到后端
   */
  private async sendCleanupNotification(type: PageCleanupData['type']): Promise<void> {
    try {
      const activeDevice = audioManager.getActiveDevice();
      if (activeDevice) {
        const data: PageCleanupData = {
          type,
          deviceId: activeDevice,
          timestamp: Date.now()
        };

        // 使用sendBeacon确保请求能够发送（即使页面正在关闭）
        if (navigator.sendBeacon) {
          const success = navigator.sendBeacon('/api/device/cleanup', JSON.stringify(data));
          if (success) {
            console.log('页面关闭通知已发送');
          } else {
            console.warn('页面关闭通知发送失败');
          }
        } else {
          // 降级方案：使用fetch
          fetch(`${ApiClient.getBaseUrl()}/device/cleanup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            keepalive: true // 确保请求在页面关闭时也能发送
          }).catch(error => {
            console.error('页面关闭通知发送失败:', error);
          });
        }
      }
    } catch (error) {
      console.error('发送清理通知失败:', error);
    }
  }

  /**
   * 手动执行清理
   */
  public async manualCleanup(): Promise<void> {
    console.log('手动执行清理工作...');
    
    try {
      await this.forceClosePTT();
      await this.stopAudioRecording();
      await this.sendCleanupNotification('page_unload');
      
      console.log('手动清理完成');
    } catch (error) {
      console.error('手动清理失败:', error);
    }
  }

  /**
   * 销毁服务
   */
  public destroy(): void {
    if (!this.isInitialized) return;

    console.log('销毁页面关闭清理服务...');

    window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.removeEventListener('blur', this.handlePageBlur.bind(this));
    window.removeEventListener('online', this.handleNetworkChange.bind(this));
    window.removeEventListener('offline', this.handleNetworkChange.bind(this));

    this.cleanupHandlers.clear();
    this.isInitialized = false;

    console.log('页面关闭清理服务已销毁');
  }
}

export const pageCleanupService = PageCleanupService.getInstance();
